import { useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";

import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import { TERRAGRUNT_TOOL_INFO_TOOLTIP, TERRAGRUNT_TOOL_OPTIONS } from "constants/terragruntTools";
import Box from "ds/components/Box";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Select from "ds/components/Select";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import useTypedContext from "hooks/useTypedContext";
import { TerragruntTool } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import Badge from "ds/components/Badge";
import Card from "ds/components/Card";

import { StackFormContext } from "../../context";
import { StackVendorFormFields } from "../../types";
import useStackCreationAnalyticsVersion from "../../useStackCreationAnalyticsVersion";
import { getSwitchToggleAnalyticsProps, getTooltipAnalyticsProps } from "../../utils";
import VersionInput from "../VersionInput";
import VersionTooltipContent from "../VersionTooltipContent";
import { getValidateTerragruntVersion } from "./getValidateTerragruntVersion";
import { getValidateToolVersion } from "./getValidateToolVersion";
import useAsyncTerragruntVersionValidation from "./useAsyncTerragruntVersionValidation";

type NewStackVendorTerragruntProps = {
  terraformVersions?: string[];
  openTofuVersions?: string[];
  terragruntVersions?: string[];
  reloadVersionsData: () => void;
  reloadLoading: boolean;
};

const NewStackVendorTerragrunt = ({
  terraformVersions,
  openTofuVersions,
  terragruntVersions,
  reloadVersionsData,
  reloadLoading,
}: NewStackVendorTerragruntProps) => {
  const analyticsVersion = useStackCreationAnalyticsVersion();
  const { setAsyncValidationLoading } = useTypedContext(StackFormContext);
  const { control, trigger, watch, setValue } = useFormContext<StackVendorFormFields>();
  useAsyncTerragruntVersionValidation(setAsyncValidationLoading);

  const terragruntValues = watch().terragrunt;

  const workflowVersions =
    terragruntValues.tool === TerragruntTool.OpenTofu ? openTofuVersions : terraformVersions;
  const vendorToolName =
    terragruntValues.tool === TerragruntTool.OpenTofu ? "OpenTofu" : "Terraform";
  const isManualMode = terragruntValues.tool === TerragruntTool.ManuallyProvisioned;
  const isRunAll = terragruntValues.useRunAll;

  const handleWorkflowToolChange = useCallback(
    (value: TerragruntTool) => {
      if (terragruntValues.tool === value) {
        return;
      }

      let version: string;
      if (value === TerragruntTool.TerraformFoss) {
        version = terraformVersions ? terraformVersions[0] : "";
      } else if (value === TerragruntTool.OpenTofu) {
        version = openTofuVersions ? openTofuVersions[0] : "";
      } else {
        version = "";
      }

      setValue("terragrunt.tool", value);
      setValue("terragrunt.workflowVersion", {
        value: version,
        type: terragruntValues.workflowVersion.type,
      });

      trigger();
    },
    [
      terragruntValues.tool,
      terragruntValues.workflowVersion.type,
      setValue,
      trigger,
      terraformVersions,
      openTofuVersions,
    ]
  );

  const toolController = (
    <Controller
      name="terragrunt.tool"
      control={control}
      render={({ field, fieldState }) => (
        <FormField
          error={fieldState.error?.message}
          noMargin
          label="Tool:"
          {...getTooltipAnalyticsProps("Vendor", "Tool", {
            provider: "Terragrunt",
            version: analyticsVersion,
          })}
          tooltipInfo={
            <>
              <TooltipModalTitle>Tool</TooltipModalTitle>
              <TooltipModalBody align="start">{TERRAGRUNT_TOOL_INFO_TOOLTIP}</TooltipModalBody>
            </>
          }
          tooltipInfoVariant="modal"
        >
          {({ ariaInputProps }) => (
            <Select
              value={field.value}
              items={TERRAGRUNT_TOOL_OPTIONS}
              onChange={handleWorkflowToolChange}
              isError={!!fieldState.error}
              {...ariaInputProps}
            />
          )}
        </FormField>
      )}
    />
  );

  const toolVersionController = (
    <Controller
      name="terragrunt.workflowVersion"
      control={control}
      rules={{
        validate: getValidateToolVersion(workflowVersions, vendorToolName),
      }}
      render={({ field, fieldState }) => {
        const version = field.value;
        return (
          <VersionInput
            reloadLoading={reloadLoading}
            reloadVersionsData={reloadVersionsData}
            title={`${vendorToolName} version`}
            analyticsProps={{
              location: "Vendor",
              name: "Terraform / OpenTofu version",
              provider: "Terragrunt",
              version: analyticsVersion,
            }}
            tooltipInfo={
              <>
                <TooltipModalTitle>Choose version</TooltipModalTitle>
                <TooltipModalBody align="start">
                  <VersionTooltipContent />
                  <ReadMoreDocsLink
                    docsUrl={getDocsUrl(
                      "/concepts/stack/stack-settings#opentofuterraform-specific-settings"
                    )}
                  />
                </TooltipModalBody>
              </>
            }
            errorMessage={fieldState.error?.message}
            type={version?.type}
            version={version?.value}
            supportedVersions={workflowVersions}
            onChange={field.onChange}
            ref={field.ref}
          />
        );
      }}
    />
  );

  return (
    <Box gap="x-large" direction="column">
      <Controller
        name="terragrunt.terragruntVersion"
        control={control}
        rules={{
          validate: getValidateTerragruntVersion(terragruntVersions),
        }}
        render={({ field, fieldState }) => {
          const version = field.value;
          return (
            <VersionInput
              reloadLoading={reloadLoading}
              reloadVersionsData={reloadVersionsData}
              title="Terragrunt version"
              analyticsProps={{
                location: "Vendor",
                name: "Terragrunt version",
                provider: "Terragrunt",
                version: analyticsVersion,
              }}
              tooltipInfo={
                <>
                  <TooltipModalTitle>Choose version</TooltipModalTitle>
                  <TooltipModalBody align="start">
                    <VersionTooltipContent />
                  </TooltipModalBody>
                </>
              }
              errorMessage={fieldState.error?.message}
              type={version?.type}
              version={version?.value}
              supportedVersions={terragruntVersions}
              onChange={field.onChange}
              ref={field.ref}
            />
          );
        }}
      />

      {isManualMode && toolController}
      {!isManualMode && (
        <Card contentGap="x-large">
          {toolController}
          {toolVersionController}
        </Card>
      )}

      <Box gap="large" direction="column">
        <Controller
          name="terragrunt.useSmartSanitization"
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              onChange={(arg) => {
                field.onChange(arg);
                trigger("terragrunt.workflowVersion");
              }}
              {...getSwitchToggleAnalyticsProps("Vendor", "Use smart sanitization", {
                provider: "Terragrunt",
                version: analyticsVersion,
              })}
              checked={field.value}
              title="Smart sanitization (recommended)"
              description={`If set, Spacelift will attempt to sanitize the resources created by terraform using the information provided by terraform as to which fields are "sensitive" or not`}
            />
          )}
        />

        <Controller
          name="terragrunt.useRunAll"
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              onChange={(value: boolean) => {
                field.onChange(value);
                setValue("terragrunt.prefixResourceNamesWithModuleName", value);
              }}
              {...getSwitchToggleAnalyticsProps("Vendor", "Use run all", {
                provider: "Terragrunt",
                version: analyticsVersion,
              })}
              checked={field.value}
              title="Use All Run"
              description={`Enable this property to use Terragrunt's "run-all" capabilities to execute commands across all projects in the workspace.`}
            />
          )}
        />

        <Controller
          name="terragrunt.skipReplan"
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              onChange={field.onChange}
              {...getSwitchToggleAnalyticsProps("Vendor", "Skip replan", {
                provider: "Terragrunt",
                version: analyticsVersion,
              })}
              checked={field.value}
              title="Skip replan"
              titleSuffix={<Badge variant="blue" type="strong" text="BETA" />}
              description="Skip the second planning phase during the apply stage by reusing the saved plan. Applies to both Run All and non-Run All stacks. Warning: this means any mocked_outputs referenced during planning will be applied as-is — do not enable this together with mocked_outputs unless you fully understand the implications, your apply may execute against mocked values rather than real ones."
            />
          )}
        />

        <Controller
          name="terragrunt.prefixResourceNamesWithModuleName"
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              onChange={field.onChange}
              {...getSwitchToggleAnalyticsProps(
                "Vendor",
                "Prefix resource names with module path",
                {
                  provider: "Terragrunt",
                  version: analyticsVersion,
                }
              )}
              checked={field.value}
              disabled={isRunAll}
              title="Prefix resource names with module path"
              description="When enabled, resource and output names are prefixed with the module path. Always on when Use All Run is enabled."
            />
          )}
        />

        <Controller
          name="terragrunt.useStateManagement"
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              onChange={field.onChange}
              {...getSwitchToggleAnalyticsProps("Vendor", "Manage state", {
                provider: "Terragrunt",
                version: analyticsVersion,
              })}
              checked={field.value}
              title="Manage state"
              description={`Indicates whether Spacelift should take care of the Terragrunt state`}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default NewStackVendorTerragrunt;
